import { createRouter, createWebHistory } from 'vue-router';
import { GetRouteDataByUrl, RouteUrlList, type IRouteUrlListElement } from '@/libs/RouteList';
import { useClientStore } from '@/stores/modules/clientInfo';
import * as ApiInstance from '@/libs/ApiClient';
import API from '@/api';
import isMobile from 'ismobilejs';
import * as Common from '@/libs/Common';
import { useEsunMB } from '@/composables/useEsunMB';
const routes = [
    {
        //  登入頁
        path: RouteUrlList.Index.path,
        name: RouteUrlList.Index.name,
        // component: () => import('@/components/1_LoginPage/LoginPage.vue')
        component: () => import('@/components/1_LoginPage/LoginPage.vue')
    },
    {
        //  登入頁
        path: RouteUrlList.Login.path,
        name: RouteUrlList.Login.name,
        // component: () => import('@/components/1_LoginPage/LoginPage.vue')
        component: () => import('@/components/1_LoginPage/LoginPage.vue')
    },
    {
        //  無補件項目
        path: RouteUrlList.NoItem.path,
        name: RouteUrlList.NoItem.name,
        component: () => import('@/components/3_NoItem/NoItem.vue')
    },
    {
        //  全部完成
        path: RouteUrlList.AllComplete.path,
        name: RouteUrlList.AllComplete.name,
        component: () => import('@/components/4_AllComplete/AllComplete.vue')
    },

    // 選擇補件業務
    {
        path: RouteUrlList.BusinessSelection.path,
        name: RouteUrlList.BusinessSelection.name,
        component: () => import('@/components/5_BusinessSelection/BusinessSelection.vue')
    },
    {
        path: RouteUrlList.FileUpload.path,
        name: RouteUrlList.FileUpload.name,
        component: () => import('@/components/6_FileUpload/FileUpload.vue')
    },
    {
        //  部分完成
        path: RouteUrlList.CompleteUploading.path,
        name: RouteUrlList.CompleteUploading.name,
        component: () => import('@/components/7_CompleteUploading/CompleteUploading.vue')
    },
    {
        path: RouteUrlList.StateError.path,
        name: RouteUrlList.StateError.name,
        component: () => import('@/components/StateError.vue')
    },
    {
        path: '/:pathMatch(.*)*',
        name: '404',
        component: () => import('@/components/StateError.vue')
    }
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
});
import { useLeaveProtectionStore } from '@/stores/modules/leaveProtection';
import LoginCheck from '@/api/fakedata/v1/LoginCheck';

// 在這裡設置全局守衛
router.beforeEach(async (to, from, next) => {
    const _targetPath: string = to.path.trim().toLowerCase();
    const routeData: IRouteUrlListElement = GetRouteDataByUrl(_targetPath);
    Common.ConsoleLog(`[router.beforeEach] 目標路由：${routeData?.name}（path=${_targetPath}）`);

    // 防止提開或刷新
    const leaveProtectionStore = useLeaveProtectionStore();
    if (routeData.leaveProtection === true) {
        leaveProtectionStore.setUnsavedChanges(true);
    } else {
        leaveProtectionStore.setUnsavedChanges(false);
    }

    // ── 錯誤頁永遠放行，避免登入檢查失敗時導向 StateError 又被攔截的無限迴圈 ──
    if (
        routeData.name === RouteUrlList.StateError.name ||
        _targetPath === RouteUrlList.StateError.path.trim().toLowerCase() ||
        routeData.name === RouteUrlList.Login.name ||
        _targetPath === RouteUrlList.Login.path.trim().toLowerCase()
    ) {
        Common.ConsoleLog('[router.beforeEach] 錯誤頁/登入頁，跳過登入檢查');
        next(); // 繼續導航
        return;
    }

    // window.scrollTo({ top: 0, behavior: 'smooth' });
    // ── 判斷此路由是否需要登入 ──
    if (routeData.requiredLogin === true) {
        try {
            Common.ConsoleLog('[router.beforeEach] 此路由需要登入，開始驗證…');

            const store = useClientStore();
            let isLogin = store.isLogin.valueOf();
            if (!store.routeInitState.valueOf()) {
                Common.ConsoleLog('[router.beforeEach] 首次導航，尚未初始化，開始登入檢查…');
                const isLoginFromAPI = await API.ServiceAlertLoginCheck(); // 登入檢查

                store.setRouteInit(true);
                store.setLoginState(isLoginFromAPI);
                const { isAppEmbedded } = useEsunMB();
                store.setIsWebViewState(isAppEmbedded.value); // 判斷WebView
                store.setIsMobileState(isMobile(window.navigator).any);

                Common.ConsoleLog('[router.index] 路由 : ' + routeData?.name);
                ApiInstance.GetServiceAlertInstance().ClearIdleCheckTimeoutId();
            }

            // 重新讀取 isLogin 狀態，確保與 store 同步
            isLogin = store.isLogin.valueOf();

            if (isLogin === false) {
                const { isAppEmbedded: isWebView, closeWebView } = useEsunMB();
                if (isWebView.value) {
                    closeWebView();
                    return; // WebView 環境不導航，直接關閉
                }

                return next({
                    name: RouteUrlList.Login.name,
                    query: {
                        ...to.query
                    }
                });
            } else {
                // 已登入，繼續導航
                next();
                return;
            }
        } catch (e) {
            next(); // 繼續導航
            return;
        }
    }
    next(); // 繼續導航
    return;
});

router.afterEach((to, from) => {
    // 頁面SEO優化 安全地設定 <link rel="canonical">
    SetCanonicalLink();
});

/**
 * @todo 頁面SEO優化
 * @param canonicalUrl
 * @returns
 */
function SetCanonicalLink(): boolean {
    try {
        const TRUSTED_HOSTS: RegExp[] = [/\.esunsec\.com\.tw$/, /\.testesunsec\.com\.tw$/];

        const origin: string = window.location.origin;
        const pathname: string = window.location.pathname;

        // 驗證 origin 來源
        const isValidOrigin: boolean = TRUSTED_HOSTS.some((hostRegex) => hostRegex.test(new URL(origin).hostname));
        const isValidPathname: boolean = /^\/[a-zA-Z0-9._~:/?#[\]@!$&'()*+,;=-]*$/.test(pathname);

        if (!isValidOrigin || !isValidPathname) {
            Common.ConsoleLog(`[router.index] Invalid Canonical URL detected will not set canonical link`);
            return false;
        }

        // 編碼canonicalUrl
        const canonicalUrl: string = encodeURI(origin + pathname).replace(/[\\"']/g, '');

        // 安全設置 canonical 標籤
        (function setCanonicalUrl(url: string): void {
            let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement('link');
                link.rel = 'canonical';
                document.head.appendChild(link);
            }
            link.href = url;
        })(canonicalUrl);

        return true;
    } catch (e) {
        Common.ConsoleLog(e);
        return false;
    }
}

export default router;
